import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout/Layout';
import { MarkdownRenderer } from '../components/Common/MarkdownRenderer';
import { useAuthStore } from '../store/authStore';
import { useDocsStore } from '../store/docsStore';

export const DocumentPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [linkCopied, setLinkCopied] = useState(false);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { currentDocument, isLoading, error, isOnline, fetchDocumentBySlug } = useDocsStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (slug) {
      fetchDocumentBySlug(slug);
    }
  }, [slug, fetchDocumentBySlug]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') {
        return;
      }
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        if (isAuthenticated && isOnline && currentDocument?.id) {
          navigate(`/edit/${currentDocument.id}`);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAuthenticated, isOnline, currentDocument?.id, navigate]);

  const copyLinkToClipboard = async () => {
    try {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <p>Loading...</p>
      </Layout>
    );
  }

  if (error || !currentDocument) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h1>Document Not Found</h1>
          <p>{error || 'The document you are looking for does not exist.'}</p>
          <Link to="/">Go back home</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h1 style={{ margin: 0 }}>{currentDocument.title}</h1>
                {currentDocument.is_private && (
                  <span style={{
                    background: 'var(--accent-alpha-20)',
                    color: 'var(--accent)',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: '1px solid var(--accent-alpha-30)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Private
                  </span>
                )}
                <button
                  onClick={copyLinkToClipboard}
                  style={{
                    padding: '6px 8px',
                    background: linkCopied ? '#28a745' : 'rgba(255, 255, 255, 0.1)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onMouseEnter={(e) => {
                    if (!linkCopied) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!linkCopied) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  title={linkCopied ? 'Link copied!' : 'Copy link to document'}
                >
                  {linkCopied ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="4,8 7,11 12,5" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="5" y="5" width="9" height="9" rx="1" />
                      <path d="M3 11V3a1 1 0 0 1 1-1h8" />
                    </svg>
                  )}
                </button>
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '8px' }}>
                <span>{currentDocument.folder_path}</span>
                <span style={{ margin: '0 12px' }}>•</span>
                <span>Updated {new Date(currentDocument.updated_at).toLocaleDateString()}</span>
              </div>
              {currentDocument.tags && currentDocument.tags.length > 0 && (
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                  {currentDocument.tags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        background: 'var(--bg-tertiary)',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: 'var(--text-secondary)'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          {isAuthenticated && isOnline && (
            <button
              onClick={() => navigate(`/edit/${currentDocument.id}`)}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                background: 'var(--accent)',
                color: 'white',
                fontWeight: 500
              }}
            >
              Edit
            </button>
          )}
        </div>
        
        <div style={{ background: 'var(--bg-secondary)', padding: '32px', borderRadius: '8px', border: '1px solid var(--border)' }}>
          <MarkdownRenderer content={currentDocument.content || ''} />
        </div>
      </div>
    </Layout>
  );
};
